// src/api/config.js
import axios from "axios";

const API_URL = "https://backend.suryakirangroup.in/api/";
// const API_URL = "http://127.0.0.1:8000/api/";

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
});

const pendingRequests = new Map();

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const language = localStorage.getItem('user_language') || 'en';
    config.headers['Accept-Language'] = language;
    
    const noCacheEndpoints = ['tyreEntryList', 'particularTyreHistory', 'tyre_history'];
    const shouldBypassCache = noCacheEndpoints.some(endpoint => config.url?.includes(endpoint));
    
    if (shouldBypassCache) {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }
    
    const cancelToken = axios.CancelToken.source();
    config.cancelToken = cancelToken.token;
    
    const requestKey = `${config.method}_${config.url}_${JSON.stringify(config.params || {})}`;
    config.requestKey = requestKey;
    pendingRequests.set(requestKey, cancelToken);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (response.config?.requestKey) {
      pendingRequests.delete(response.config.requestKey);
    }
    return response;
  },
  async (error) => {
    if (error.config?.requestKey) {
      pendingRequests.delete(error.config.requestKey);
    }

    if (axios.isCancel(error)) {
      return Promise.reject({ isCanceled: true });
    }

    const originalRequest = error?.config;

    if (error?.code === 'ECONNABORTED' || !error?.response) {
      if (originalRequest) {
        if (!originalRequest._retryCount) originalRequest._retryCount = 0;
        if (originalRequest._retryCount < 2) {
          originalRequest._retryCount += 1;
          await new Promise(resolve => setTimeout(resolve, 500 * originalRequest._retryCount));
          return api(originalRequest);
        }
      }
      return Promise.reject({ 
        message: 'Network error - please check your connection',
        isNetworkError: true 
      });
    }

    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      clearPendingRequests();
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      sessionStorage.removeItem('access_token');
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject({ 
        message: 'Session expired - please login again',
        isAuthError: true 
      });
    }

    if (error?.response?.status === 404) {
      return Promise.resolve({ data: [] });
    }

    return Promise.reject({
      message: error?.response?.data?.message || 'An error occurred',
      status: error?.response?.status,
      data: error?.response?.data,
      isServerError: true
    });
  }
);

export const clearPendingRequests = () => {
  for (const [key, cancelToken] of pendingRequests) {
    try {
      cancelToken.cancel('Logout - requests canceled');
    } catch (e) {}
  }
  pendingRequests.clear();
};

export default api;